// TODO: try popover 3rd party component
import {
  useState,
  useEffect,
  useRef,
  SyntheticEvent,
  forwardRef,
  useImperativeHandle,
  useId,
  useLayoutEffect,
} from 'react';
import styled from 'styled-components';
import useAutocomplete, { AutocompleteChangeReason, AutocompleteChangeDetails } from '@mui/material/useAutocomplete';
import { Typography } from '../Typography/Typography';
import ChevronDown from '../../assets/icons/chevronDown.svg?react';
import Search from '../../assets/icons/search.svg?react';
import Cancel from '../../assets/icons/cancel.svg?react';
import Checked from '../../assets/icons/checked.svg?react';
import Unchecked from '../../assets/icons/unchecked.svg?react';
import { dropdownTriggerStyles, focusVisibleStyles, popoverBoxshadow } from '../../sharedStyles';

const BORDER_RADIUS = '10px';
const EMPTY_OPTIONS_HEIGHT = '152px'; // height of the menu when there are no options
const MAX_MENU_HEIGHT = 452; // width of the label when there are no options

const StyledMultiselectAutocomplete = styled.div`
  box-sizing: border-box;
  width: fit-content;
  position: relative;
`;

const DropdownButton = styled(Typography).attrs({ styledAs: 'bodySmallDMSans', renderedAs: 'button' })<{
  $focused: boolean;
  $hasSelection: boolean;
}>`
  ${dropdownTriggerStyles}

  ${({ $focused }) => ($focused ? 'background-color: var(--grayscale-50);' : '')}
  ${({ $focused }) => ($focused ? 'border-color: var(--grayscale-50);' : '')}

  ${({ $hasSelection }) => ($hasSelection ? 'border-color: var(--purple-200);' : '')}
  // !important overrides hover background style
  ${({ $hasSelection }) => ($hasSelection ? 'background-color: var(--purple-200) !important;' : '')}
`;

const NumValuesSelectedTag = styled(Typography)`
  display: flex;
  width: 16px;
  height: 16px;
  line-height: 100%; // keep the 1 in the circle vertically centered
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  background: var(--grayscale-900);
  color: var(--purple-200);
`;

const DropdownMenu = styled.div<{
  $visible: boolean;
  $verticalAlign: 'bottom' | 'top';
  $horizontalAlign: 'left' | 'right';
}>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  position: absolute;

  ${({ $horizontalAlign }) => $horizontalAlign}: 0;
  // the height of the button should be 32px and the distance between the button and menu should be 6px
  ${({ $verticalAlign }) => ($verticalAlign === 'bottom' ? 'transform: translateY(6px);' : 'bottom: 38px;')}

  box-sizing: border-box;
  width: 360px;
  min-height: ${EMPTY_OPTIONS_HEIGHT};
  padding-top: 12px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: ${BORDER_RADIUS};
  border: 1px solid var(--grayscale-100);
  background: var(--white);
  ${popoverBoxshadow}
  z-index: 100;
  pointer-events: auto;
`;

const DropdownMenuContents = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputAndOptions = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TextFieldWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 0 12px;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 8px;
`;

const CancelButton = styled.button`
  all: unset;
  cursor: pointer;
  position: absolute;
  right: 8px;
  color: var(--white);
  background-color: var(--grayscale-600);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${focusVisibleStyles}
`;

const TextField = styled.input`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  // the horizontal padding leaves space for the icons
  padding: 7px 30px;
  align-items: center;
  border-radius: var(--border-radius-medium);
  // using box-shadow instead of border so it doesn't "jump" when we bump
  // the border width on focus
  box-shadow: 0 0 0 1px var(--grayscale-100);
  border: none;
  transition: box-shadow 0.2s ease-out;

  &:focus-visible {
    // transparent outline for windows high contrast mode
    outline: rgba(0, 0, 0, 0);
    box-shadow: 0 0 0 2px var(--purple-700);
  }
`;

const DropdownList = styled.ul`
  margin-block-start: 0;
  padding-inline-start: 0;
  margin-bottom: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 8px 12px;
`;

const DropdownOption = styled(Typography)`
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 8px 0;
  // for the focus outline
  border-radius: var(--border-radius-small);

  ${focusVisibleStyles}

  > svg {
    color: var(--purple-700);
    flex-shrink: 0;
  }
`;

const NoResults = styled(Typography)`
  // TODO: use constant for 12px, calculaate the 16px
  padding: 16px 12px;
`;

const DropdownFooter = styled.div`
  border-top: 1px solid var(--grayscale-100);
  background-color: var(--white);
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 12px 12px 0px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  bottom: 0;
  border-radius: 0 0 ${BORDER_RADIUS} ${BORDER_RADIUS};
`;

const ClearButton = styled(Typography).attrs({ styledAs: 'bodyExtraSmallDMSans', renderedAs: 'button', weight: 500 })`
  all: unset;
  cursor: pointer;
  display: flex;
  box-sizing: border-box;
  height: 32px;
  padding: 0px 12px;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius-medium);
  background: var(--grayscale-50);
  transition: background-color 0.2s ease-out;

  &:hover {
    background-color: var(--grayscale-100);
  }

  ${focusVisibleStyles}

  &:disabled {
    cursor: auto;
    color: var(--grayscale-300);
    background: var(--grayscale-50);
  }
`;

export type MultiselectAutocompleteOption = {
  value: string;
  description: string;
};

export type MultiselectAutocompleteOnChange = (
  event: SyntheticEvent<Element, Event>,
  value: MultiselectAutocompleteOption[],
  // Added for completeness to match MUI's api
  reason: AutocompleteChangeReason,
  // Added for completeness to match MUI's api
  details?: AutocompleteChangeDetails<MultiselectAutocompleteOption> | undefined,
) => void;

export type MultiselectAutocompleteProps = {
  options: MultiselectAutocompleteOption[];
  label: string;
  onChange: MultiselectAutocompleteOnChange;
  className?: string;
  initialValue?: MultiselectAutocompleteOption[];
  horizontalAlign?: 'auto' | 'left' | 'right';
  verticalAlign?: 'auto' | 'bottom' | 'top';
};

export type MultiselectAutocompleteHandle = {
  clear: () => void;
};

/**
 * Multiselect component with autocomplete input field.
 *
 * TODO: focus trap on the dropdown menu or use a 3rd party popover component
 */
export const MultiselectAutocomplete = forwardRef<MultiselectAutocompleteHandle, MultiselectAutocompleteProps>(
  (
    {
      options,
      label,
      onChange,
      className,
      initialValue = [],
      horizontalAlign = 'auto',
      verticalAlign = 'auto',
      // Allow passing through additional props to the container, e.g. analytics props
      ...rest
    },
    ref,
  ) => {
    // We have to manually control the visibility of the dropdown because the input field,
    // which is used as the trigger in the MUI component, is inside the hidden area with
    // the dropdown options
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [computedHorizontalAlign, setComputedHorizontalAlign] = useState<'left' | 'right'>(
      horizontalAlign === 'right' ? 'right' : 'left',
    );
    const [computedVerticalAlign, setComputedVerticalAlign] = useState<'bottom' | 'top'>(
      verticalAlign === 'top' ? 'top' : 'bottom',
    );
    // Tracking the input value ourselves allows us to clear the input
    const [inputValue, setInputValue] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownMenuRef = useRef<HTMLDivElement>(null);
    const componentId = useId();

    const {
      getRootProps,
      getInputLabelProps,
      getInputProps,
      getListboxProps,
      getOptionProps,
      groupedOptions,
      getClearProps,
      value,
      focused,
      setAnchorEl,
    } = useAutocomplete({
      id: componentId,
      defaultValue: initialValue,
      inputValue,
      onInputChange: (_, newValue) => setInputValue(newValue),
      multiple: true,
      options,
      getOptionLabel: (option: MultiselectAutocompleteOption) => option.description,
      disableCloseOnSelect: true,
      // Forces the MUI dropdown to always be open because we have our own dropdown logic.
      // If `open` were not set to `true`, the user would have to open the dropdown twice
      open: true,
      // Allows keyboard users to tab from the input field to the options list without
      // the search input being cleared
      clearOnBlur: false,
      onChange,
      isOptionEqualToValue: (option: MultiselectAutocompleteOption, value: MultiselectAutocompleteOption) => {
        return option.value === value.value;
      },
    });

    useImperativeHandle(ref, () => ({
      clear: () => {
        setInputValue('');
        const clearProps = getClearProps();
        if (clearProps.onClick) {
          clearProps.onClick({} as React.MouseEvent<HTMLButtonElement>);
        }
      },
    }));

    // TODO: set focus back on the trigger button using triggerRef.current after automatically closing the menu
    const closeDropdownOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDropdownVisible(false);
      }
    };
    const closeDropdownOnOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', closeDropdownOnOutsideClick);
      document.addEventListener('keyup', closeDropdownOnEsc);

      return () => {
        document.removeEventListener('mousedown', closeDropdownOnOutsideClick);
        document.removeEventListener('keyup', closeDropdownOnEsc);
      };
    }, []);

    useEffect(() => {
      const updateMaxHeight = () => {
        if (dropdownMenuRef.current && dropdownVisible) {
          const windowHeight = window.innerHeight;
          const rect = dropdownMenuRef.current.getBoundingClientRect();

          let dynamicMaxHeight: number;

          if (computedVerticalAlign === 'top') {
            // When dropdown opens above trigger, calculate from bottom of dropdown
            const elementBottom = rect.bottom;
            // 8px is the distance we want between the dropdown menu and the edge of the screen
            dynamicMaxHeight = elementBottom - 8;
          } else {
            // When dropdown opens below trigger, calculate from top of dropdown
            const elementTop = rect.top;
            // 8px is the distance we want between the dropdown menu and the edge of the screen
            dynamicMaxHeight = windowHeight - elementTop - 8;
          }

          // Use the smaller of MAX_MENU_HEIGHT or the calculated height, clamped at 0 to prevent negative values
          const finalMaxHeight = Math.min(MAX_MENU_HEIGHT, Math.max(0, dynamicMaxHeight));

          dropdownMenuRef.current.style.maxHeight = `${finalMaxHeight}px`;
        }
      };

      updateMaxHeight();

      window.addEventListener('resize', updateMaxHeight);

      return () => {
        window.removeEventListener('resize', updateMaxHeight);
      };
    }, [dropdownVisible, computedVerticalAlign]);

    useLayoutEffect(() => {
      if (!dropdownVisible) {
        return;
      }

      const updateAlignment = () => {
        if ((!triggerRef.current && !wrapperRef.current) || !dropdownMenuRef.current) {
          return;
        }

        const triggerElement = triggerRef.current ?? wrapperRef.current;
        // TypeScript doesn't recognize that the early return above guarantees at least one ref exists
        if (!triggerElement) {
          return;
        }
        const triggerRect = triggerElement.getBoundingClientRect();
        const menuRect = dropdownMenuRef.current.getBoundingClientRect();

        if (horizontalAlign === 'auto') {
          const availableRight = window.innerWidth - triggerRect.right;
          const availableLeft = triggerRect.left;
          const nextHorizontalAlign =
            availableRight >= menuRect.width || availableRight >= availableLeft ? 'left' : 'right';

          setComputedHorizontalAlign((current) => (current === nextHorizontalAlign ? current : nextHorizontalAlign));
        } else {
          setComputedHorizontalAlign((current) => (current === horizontalAlign ? current : horizontalAlign));
        }

        if (verticalAlign === 'auto') {
          const availableBottom = window.innerHeight - triggerRect.bottom;
          const availableTop = triggerRect.top;
          // Use the expected capped height (452px max) when deciding direction to avoid
          // unnecessarily opening upward when the constrained menu would fit below
          const expectedMenuHeight = Math.min(menuRect.height, 452);
          const nextVerticalAlign =
            availableBottom >= expectedMenuHeight || availableBottom >= availableTop ? 'bottom' : 'top';

          setComputedVerticalAlign((current) => (current === nextVerticalAlign ? current : nextVerticalAlign));
        } else {
          setComputedVerticalAlign((current) => (current === verticalAlign ? current : verticalAlign));
        }
      };

      updateAlignment();

      window.addEventListener('resize', updateAlignment);
      window.addEventListener('scroll', updateAlignment, true);

      return () => {
        window.removeEventListener('resize', updateAlignment);
        window.removeEventListener('scroll', updateAlignment, true);
      };
    }, [dropdownVisible, horizontalAlign, verticalAlign, groupedOptions.length]);

    // Focus the input when dropdown opens using MUI's ref from getInputProps
    useEffect(() => {
      if (dropdownVisible) {
        const inputProps = getInputProps();
        const inputRef = inputProps.ref as React.RefObject<HTMLInputElement> | null;
        if (inputRef?.current) {
          inputRef.current.focus();
        }
      }
    }, [dropdownVisible]);

    return (
      <StyledMultiselectAutocomplete ref={wrapperRef} className={className} {...getRootProps()} {...rest}>
        <DropdownButton
          {...getInputLabelProps()}
          aria-expanded={dropdownVisible}
          $focused={focused || dropdownVisible}
          $hasSelection={value.length > 0}
          weight={value.length > 0 ? 500 : undefined}
          onClick={() => {
            setDropdownVisible(!dropdownVisible);
          }}
          ref={triggerRef}
        >
          {label}
          {value.length > 0 && (
            <NumValuesSelectedTag styledAs="bodyExtraExtraSmallDMSans" renderedAs={'span'} weight={600}>
              {value.length}
            </NumValuesSelectedTag>
          )}
          <ChevronDown aria-hidden={true} />
        </DropdownButton>

        <DropdownMenu
          ref={dropdownMenuRef}
          $visible={dropdownVisible}
          $verticalAlign={computedVerticalAlign}
          $horizontalAlign={computedHorizontalAlign}
        >
          <DropdownMenuContents>
            <InputAndOptions>
              <TextFieldWrapper ref={setAnchorEl}>
                <SearchIcon aria-hidden={true} />

                {inputValue.length ? (
                  <CancelButton onClick={() => setInputValue('')}>
                    <Cancel title="clear search text" height="7px" width="7px" />
                  </CancelButton>
                ) : null}

                <TextField {...getInputProps()} placeholder="Search" />
              </TextFieldWrapper>

              {groupedOptions.length > 0 ? (
                <DropdownList {...getListboxProps()}>
                  {groupedOptions.map((option, index: number) => {
                    const optionData = option as MultiselectAutocompleteOption;
                    const { ...optionProps } = getOptionProps({ option: optionData, index });
                    const isSelected = value.find(
                      (valueItem) =>
                        valueItem.description === optionData.description && valueItem.value === optionData.value,
                    );
                    return (
                      <DropdownOption
                        styledAs="bodySmallDMSans"
                        renderedAs="li"
                        {...optionProps}
                        tabIndex={0}
                        onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) => {
                          if (event.key === ' ' || event.key === 'Enter') {
                            event.preventDefault();
                            // We're reusing the mouse onClick event on keyboard interaction because
                            // for some reason this functionality isn't added automatically
                            // @ts-expect-error Argument of type 'KeyboardEvent<HTMLLIElement>' is not assignable to parameter of type 'MouseEvent<HTMLLIElement, MouseEvent>'.
                            optionProps.onClick(event);
                          }
                        }}
                      >
                        {isSelected ? <Checked aria-hidden /> : <Unchecked aria-hidden />}
                        <Typography styledAs="bodySmallDMSans">{optionData.description}</Typography>
                      </DropdownOption>
                    );
                  })}
                </DropdownList>
              ) : (
                <NoResults styledAs="bodySmallDMSans">No results</NoResults>
              )}
            </InputAndOptions>
          </DropdownMenuContents>

          <DropdownFooter>
            <ClearButton {...getClearProps()} tabIndex={0} disabled={!value.length}>
              Clear
            </ClearButton>
          </DropdownFooter>
        </DropdownMenu>
      </StyledMultiselectAutocomplete>
    );
  },
);
